import { FC, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { FormInputText, FormDropdown } from '../../components/form';
import { RuleGroup } from '../waterfall-editor/RuleGroup';
import { SettingDataEditor } from './SettingDataEditor';
import { FeeRuleViewModel, FeeRuleType, SettingDataViewModel } from '../../models/fees';
import { SimpleExpression, FieldMetadata, OperatorInfo, MetadataContextType, LogicalOperator } from '../../models/expression';
import * as expressionService from '../../services/expressionService';
import * as configurationService from '../../services/configurationService';
import { mergeCustomFieldsWithMetadata } from '../../utils/custom-field-helper';

interface FeeRuleEditorDialogProps {
  rule: FeeRuleViewModel | null;
  visible: boolean;
  onClose: () => void;
  onSave: (rule: FeeRuleViewModel) => void;
  ruleType: FeeRuleType | null;
}

interface FeeRuleFormData {
  description: string | null;
  ruleType: FeeRuleType | null;
  settingData: SettingDataViewModel[] | null;
}

export const FeeRuleEditorDialog: FC<FeeRuleEditorDialogProps> = ({ rule, visible, onClose, onSave, ruleType }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fieldMetadata, setFieldMetadata] = useState<FieldMetadata[]>([]);
  const [settingDataFieldMetadata, setSettingDataFieldMetadata] = useState<FieldMetadata[]>([]);
  const [operators, setOperators] = useState<OperatorInfo[]>([]);
  const [expression, setExpression] = useState<SimpleExpression | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FeeRuleFormData>({
    defaultValues: {
      description: rule?.description || null,
      ruleType: rule?.ruleType || ruleType || FeeRuleType.Initial,
      settingData: rule?.settingData || (
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.Initial || 
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.Final ||
        (rule?.ruleType || ruleType || FeeRuleType.Initial) === FeeRuleType.RequestValue
          ? [{ fieldId: null, value: null }]
          : null
      ),
    },
  });

  const selectedRuleType = watch('ruleType');
  const formDescription = watch('description');
  const formSettingData = watch('settingData');

  useEffect(() => {
    if (visible) {
      const fetchMetadata = async () => {
        setIsLoading(true);
        try {
          const currentRuleType = selectedRuleType || ruleType || FeeRuleType.Initial;
          let contextType: MetadataContextType;
          let targetContextType: MetadataContextType | null = null;
          
          switch (currentRuleType) {
            case FeeRuleType.Initial:
              contextType = MetadataContextType.FeeInitial;
              targetContextType = MetadataContextType.FeeInitialTarget;
              break;
            case FeeRuleType.RequestValue:
              contextType = MetadataContextType.FeeRequestValue;
              targetContextType = MetadataContextType.FeeRequestValueTarget;
              break;
            case FeeRuleType.Final:
              contextType = MetadataContextType.FeeFinal;
              targetContextType = MetadataContextType.FeeFinalTarget;
              break;
            case FeeRuleType.IncludeFee:
              contextType = MetadataContextType.FeeIncludeFee;
              targetContextType = null;
              break;
            default:
              contextType = MetadataContextType.FeeInitial;
              targetContextType = MetadataContextType.FeeInitialTarget;
          }

          const promises: [
            Promise<FieldMetadata[]>,
            Promise<OperatorInfo[]>,
            Promise<any[]>,
            Promise<FieldMetadata[]> | Promise<never[]>
          ] = [
            expressionService.getFieldMetadata(contextType),
            expressionService.getOperators(),
            configurationService.getAllCustomFields(),
            targetContextType ? expressionService.getFieldMetadata(targetContextType) : Promise.resolve([]),
          ];

          const [fields, allOperators, customFields, targetFields] = await Promise.all(promises);

          const mergedFields = mergeCustomFieldsWithMetadata(fields, customFields);

          setFieldMetadata(mergedFields);
          setSettingDataFieldMetadata(targetFields);

          // TODO: "Not", "And", "Or" are logical operators - backend strips binary expressions using them. Use Equal/NotEqual/etc for comparisons.
          const filteredOperators = allOperators.filter(
            (op) => op.isLogical && !['Not', 'And', 'Or'].includes(op.operator)
          );
          setOperators(filteredOperators);
        } catch (error) {
          console.error('Failed to fetch field metadata:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchMetadata();
    }
  }, [visible, selectedRuleType, ruleType]);

  useEffect(() => {
    if (visible && rule) {
      setExpression(
        rule.expression || {
          expressions: [],
          logicalOperator: LogicalOperator.And,
          isNot: false,
        }
      );
      reset({
        description: rule.description || null,
        ruleType: rule.ruleType || ruleType || FeeRuleType.Initial,
        settingData: rule.settingData || null,
      });
    } else if (visible && !rule) {
      setExpression({
        expressions: [],
        logicalOperator: LogicalOperator.And,
        isNot: false,
      });
      const initialRuleType = ruleType || FeeRuleType.Initial;
      reset({
        description: null,
        ruleType: initialRuleType,
        settingData: (initialRuleType === FeeRuleType.Initial || initialRuleType === FeeRuleType.Final || initialRuleType === FeeRuleType.RequestValue)
          ? [{ fieldId: null, value: null }]
          : null,
      });
    }
  }, [visible, rule, ruleType, reset]);

  useEffect(() => {
    if (selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) {
      if (!formSettingData || formSettingData.length === 0) {
        setValue('settingData', [{ fieldId: null, value: null }]);
      }
    }
  }, [selectedRuleType, formSettingData, setValue]);

  const ruleTypeOptions = useMemo(() => {
    return Object.values(FeeRuleType).map((type) => ({
      label: type,
      value: type,
    }));
  }, []);

  const isFormValid = useMemo(() => {
    const hasExpression = expression && 
                         expression.expressions && 
                         expression.expressions.length > 0 &&
                         expression.expressions.some(item => item.expression || item.expressionGroup);

    if (selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) {
      if (!formSettingData || formSettingData.length === 0) {
        return false;
      }

      const hasAtLeastOneComplete = formSettingData.some(
        (setting) => setting.fieldId && setting.value != null && String(setting.value).trim() !== ''
      );
      
      return hasExpression && hasAtLeastOneComplete;
    }

    return hasExpression;
  }, [selectedRuleType, formSettingData, expression]);

  const onSubmit = (data: FeeRuleFormData) => {
    const filteredSettingData = data.settingData
      ?.filter((setting) => setting.fieldId && setting.value != null && String(setting.value).trim() !== '')
      .map((setting) => ({
        fieldId: setting.fieldId,
        value: String(setting.value),
      })) || null;

    const updatedRule: FeeRuleViewModel = {
      ruleId: rule?.ruleId || '',
      description: data.description,
      expression: expression,
      ruleType: data.ruleType,
      settingData: filteredSettingData && filteredSettingData.length > 0 ? filteredSettingData : null,
    };
    onSave(updatedRule);
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" className="p-button-secondary" onClick={onClose} />
      <Button 
        label="Save" 
        className="p-button-primary" 
        onClick={handleSubmit(onSubmit)} 
        disabled={!isFormValid}
      />
    </div>
  );

  return (
    <Dialog
      header={rule ? 'Edit Rule' : 'Add Rule'}
      visible={visible}
      onHide={onClose}
      footer={footer}
      style={{ width: '90vw', maxWidth: '1200px' }}
      className="fee-rule-editor-dialog"
    >
      {isLoading ? (
        <div className="flex flex-column align-items-center justify-content-center gap-2" style={{ minHeight: '200px' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
          <span>Loading metadata...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-column gap-4">
            <div style={{ width: '375px' }}>
              <FormInputText
                control={control}
                errors={errors}
                field="description"
                label="Description"
                placeholder="Enter rule description"
                className="flex flex-column"
                inputClassName="border-1 border-solid border-gray-300 w-full"
              />
            </div>

            <div style={{ width: '375px' }}>
              <FormDropdown
                control={control}
                errors={errors}
                field="ruleType"
                label="Rule Type"
                options={ruleTypeOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Select rule type"
                className="flex flex-column"
                inputClassName="border-1 border-solid border-gray-300 w-full"
              />
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-medium">
                Expression<span className="text-red-500">*</span>
              </label>
              {expression && (
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                  <TabPanel header="Editor">
                    <RuleGroup
                      group={expression}
                      level={0}
                      onUpdate={(updatedExpression) => setExpression(updatedExpression)}
                      fieldMetadata={fieldMetadata}
                      operators={operators}
                    />
                  </TabPanel>
                  <TabPanel header="JSON">
                    <pre className="bg-gray-100 p-3 border-round overflow-auto" style={{ maxHeight: '400px' }}>
                      {JSON.stringify(expression, null, 2)}
                    </pre>
                  </TabPanel>
                </TabView>
              )}
            </div>

            {(selectedRuleType === FeeRuleType.Initial || selectedRuleType === FeeRuleType.Final || selectedRuleType === FeeRuleType.RequestValue) && (
              <SettingDataEditor 
                control={control} 
                errors={errors} 
                setValue={setValue} 
                fieldMetadata={settingDataFieldMetadata}
                ruleType={selectedRuleType}
              />
            )}
          </div>
        </form>
      )}
    </Dialog>
  );
};

